import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import {
  Radio,
  House,
  MagnifyingGlass,
  Heart,
  User,
  SignOut,
  GearSix,
  Bell,
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';

const Navbar = () => {
  const { user, logout } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    let isMounted = true;

    const loadNotifications = async () => {
      try {
        const response = await api.getNotifications({ limit: 20 });

        if (!isMounted) return;

        setUnreadCount(response.data?.unread_count || 0);
      } catch (error) {
        console.error('Failed to load notifications:', error);
      }
    };

    loadNotifications();

    const interval = setInterval(loadNotifications, 30000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navLinks = [
    { path: '/discover', icon: House, label: 'Discover' },
    { path: '/search', icon: MagnifyingGlass, label: 'Search' },
    { path: '/favorites', icon: Heart, label: 'Favorites' },
  ];

  return (
    <nav className="sticky top-0 z-50 backdrop-filter backdrop-blur-2xl bg-[#050505]/80 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/discover" className="flex items-center gap-3 group">
            <Radio size={32} weight="duotone" className="text-cyan-400" />
            <span
              className="text-xl font-black tracking-tighter text-white group-hover:text-cyan-400 transition-colors"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              WORLDofRADIO
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.path;

              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  className={`flex items-center gap-2 text-sm font-semibold transition-colors ${isActive
                      ? 'text-cyan-400'
                      : 'text-gray-300 hover:text-cyan-400'
                    }`}
                >
                  <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* User Menu */}
          {user ? (
            <div className="flex items-center gap-4">
              <button
                type="button"
                data-testid="notifications-btn"
                onClick={() => navigate('/notifications')}
                className={`relative w-10 h-10 rounded-full border transition-all flex items-center justify-center ${location.pathname === '/notifications'
                    ? 'border-cyan-400 bg-cyan-400/10 text-cyan-400'
                    : 'border-white/10 bg-white/5 text-gray-300 hover:text-cyan-400 hover:border-cyan-400/50'
                  }`}
                aria-label="Notifications"
              >
                <Bell
                  size={22}
                  weight={unreadCount > 0 ? 'fill' : 'regular'}
                />

                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-cyan-400 text-black text-[11px] font-black flex items-center justify-center shadow-[0_0_10px_rgba(34,211,238,0.45)]">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-btn"
                    className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                  >
                    <span className="text-sm text-gray-300 hidden sm:block">
                      {user.name || user.display_name || user.username}
                    </span>

                    <Avatar className="w-10 h-10 border-2 border-cyan-500/50">
                      <AvatarImage
                        src={user.avatar}
                        alt={user.name || user.display_name || user.username}
                      />
                      <AvatarFallback className="bg-cyan-500/20 text-cyan-400 font-bold">
                        {(user.name || user.display_name || user.username || 'U')
                          ?.charAt(0)
                          .toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="end"
                  className="w-56 bg-[#0A0E17] border-white/10 text-white"
                >
                  <DropdownMenuItem
                    data-testid="nav-profile"
                    onClick={() => navigate('/profile')}
                    className="cursor-pointer hover:bg-white/5 focus:bg-white/5"
                  >
                    <User size={16} className="mr-2" />
                    My Profile
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    data-testid="nav-settings"
                    onClick={() => navigate('/settings')}
                    className="cursor-pointer hover:bg-white/5 focus:bg-white/5"
                  >
                    <GearSix size={16} className="mr-2" />
                    Settings
                  </DropdownMenuItem>

                  {user.role === 'admin' && (
                    <>
                      <DropdownMenuSeparator className="bg-white/10" />

                      <DropdownMenuItem
                        data-testid="nav-admin"
                        onClick={() => navigate('/admin')}
                        className="cursor-pointer hover:bg-white/5 focus:bg-white/5 text-cyan-400"
                      >
                        <GearSix size={16} className="mr-2" />
                        Admin Dashboard
                      </DropdownMenuItem>
                    </>
                  )}

                  <DropdownMenuSeparator className="bg-white/10" />

                  <DropdownMenuItem
                    data-testid="nav-logout"
                    onClick={handleLogout}
                    className="cursor-pointer hover:bg-white/5 focus:bg-white/5 text-red-400"
                  >
                    <SignOut size={16} className="mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Button
                data-testid="nav-login"
                onClick={() => navigate('/login')}
                variant="ghost"
                className="text-gray-300 hover:text-cyan-400"
              >
                Log in
              </Button>

              <Button
                data-testid="nav-signup"
                onClick={() => navigate('/register')}
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full"
              >
                Sign up
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 backdrop-filter backdrop-blur-2xl bg-[#050505]/90 border-t border-white/10 px-4 py-3">
        <div className="flex items-center justify-around">
          {navLinks.map((link) => {
            const Icon = link.icon;
            const isActive = location.pathname === link.path;

            return (
              <Link
                key={link.path}
                to={link.path}
                className={`flex flex-col items-center gap-1 transition-colors ${isActive ? 'text-cyan-400' : 'text-gray-400'
                  }`}
              >
                <Icon size={24} weight={isActive ? 'fill' : 'regular'} />
                <span className="text-[11px] font-medium">{link.label}</span>
              </Link>
            );
          })}

          <Link
            to="/notifications"
            className={`relative flex flex-col items-center gap-1 transition-colors ${location.pathname === '/notifications'
                ? 'text-cyan-400'
                : 'text-gray-400'
              }`}
          >
            <div className="relative">
              <Bell
                size={24}
                weight={
                  location.pathname === '/notifications' || unreadCount > 0
                    ? 'fill'
                    : 'regular'
                }
              />

              {unreadCount > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[17px] h-[17px] px-1 rounded-full bg-cyan-400 text-black text-[10px] font-black flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </div>

            <span className="text-[11px] font-medium">News</span>
          </Link>

          <Link
            to="/profile"
            className={`flex flex-col items-center gap-1 transition-colors ${location.pathname === '/profile'
                ? 'text-cyan-400'
                : 'text-gray-400'
              }`}
          >
            <User
              size={24}
              weight={location.pathname === '/profile' ? 'fill' : 'regular'}
            />
            <span className="text-[11px] font-medium">Profile</span>
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;